// ====================================================================
// Agent Mode — drawer client
// --------------------------------------------------------------------
// Ports the chat client from templates/page-agent-chat.php into a
// reusable module wired to the global Agent Mode shell. The drawer
// markup is rendered by partials/agent-mode.php; config travels in via
// the localized wlAgentConfig global.
//
// Behavior:
//   - Triggers (.agent-trigger, [data-agent-mode-open]) open the drawer.
//   - First open in a session replays prior turns from the conversation
//     endpoint so the chat picks up where it left off across pages.
//   - First user submit transitions the shell from --starter to --active
//     (input docks to the bottom, chips disappear, log appears).
//   - SSE stream events fan out into chat bubbles, activity indicator
//     updates, and tile grids — same wire format as the test page.
// ====================================================================

(function () {
  var cfg = window.wlAgentConfig;
  if (!cfg) return;

  var root = document.getElementById('agent-mode');
  if (!root) return;

  // ------------------------------------------------------------------
  // DOM refs (all live inside the dormant agent-mode partial)
  // ------------------------------------------------------------------

  var log        = root.querySelector('[data-agent-log]');
  var starterFm  = root.querySelector('[data-agent-form="starter"]');
  var dockFm     = root.querySelector('[data-agent-form="dock"]');
  var starterIn  = starterFm ? starterFm.querySelector('[data-agent-input]') : null;
  var dockIn     = dockFm ? dockFm.querySelector('[data-agent-input]') : null;
  var chips      = root.querySelectorAll('[data-agent-chip]');
  var exitBtns   = root.querySelectorAll('[data-agent-mode-close]');
  var newBtns    = root.querySelectorAll('[data-agent-mode-new]');

  // Triggers live OUTSIDE the drawer in the page DOM. Multiple may exist
  // on a page (bubble + hero CTA, etc.) so query the document.
  var openBtns   = document.querySelectorAll('[data-agent-mode-open]');

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  var sessionId = null;
  try {
    sessionId = sessionStorage.getItem('hahn_ai_session') || null;
  } catch (e) { /* sessionStorage unavailable (privacy mode) — fine */ }

  var abortCtl       = null;          // current SSE fetch
  var hydrated       = false;         // have we tried to replay prior turns yet?
  var hasMessages    = false;         // any user message rendered? (drives --starter vs --active)
  var leadCaptured   = false;         // lead already submitted this conversation?
  var busy           = false;         // a response is currently streaming (input locked)
  var lastFocusedEl  = null;          // for focus restoration on close

  // ------------------------------------------------------------------
  // Open / close
  // ------------------------------------------------------------------

  function openDrawer() {
    lastFocusedEl = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('agent-mode-open');
    setMode(hasMessages ? 'active' : 'starter');

    // Lazy-hydrate prior conversation. Only run once per page load; if
    // the user closes and reopens, we already have the messages.
    if (!hydrated) {
      hydrated = true;
      hydrateConversation();
    }

    // Focus the active input. Defer one frame so the slide transition
    // doesn't fight the focus jump.
    requestAnimationFrame(function () {
      var input = currentInput();
      if (input) input.focus();
    });
  }

  function closeDrawer() {
    // Abort any in-flight stream so the response doesn't keep rendering
    // into a closed drawer; sessionStorage still holds the session id
    // and the conversation log persists server-side.
    if (abortCtl) { try { abortCtl.abort(); } catch (e) {} abortCtl = null; }
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('agent-mode-open');

    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      try { lastFocusedEl.focus(); } catch (e) {}
    }
  }

  function setMode(mode) {
    root.classList.toggle('agent-mode--starter', mode === 'starter');
    root.classList.toggle('agent-mode--active',  mode === 'active');
  }

  // Single point of truth for the "is there content?" state. Keeping
  // the boolean and the CSS class in sync here means the "New
  // conversation" button visibility (driven off .agent-mode--active)
  // never desyncs from JS state.
  function setHasMessages(val) {
    hasMessages = !!val;
  }

  // Lock the UI while a response is streaming — the visitor can't send
  // another message until the agent has fully finished. Disables both
  // inputs + send buttons and flags the drawer for CSS (grayed look).
  function setBusy(state) {
    busy = !!state;
    root.classList.toggle('agent-mode--busy', busy);
    [starterIn, dockIn].forEach(function (el) { if (el) el.disabled = busy; });
    [starterFm, dockFm].forEach(function (fm) {
      if (!fm) return;
      var send = fm.querySelector('.agent-mode__send');
      if (send) send.disabled = busy;
    });
  }

  function currentInput() {
    return hasMessages ? dockIn : starterIn;
  }

  // Reset the chat back to the starter state. Triggered by the "New
  // conversation" button. Local state only — the previous conversation
  // row stays in the DB for the admin Conversations view and lead
  // attribution. The visitor's next message gets a fresh session_id
  // from the streaming controller.
  function startNewConversation() {
    if (abortCtl) {
      try { abortCtl.abort(); } catch (e) {}
      abortCtl = null;
    }
    try { sessionStorage.removeItem('hahn_ai_session'); } catch (e) {}
    sessionId = null;
    hydrated  = true; // we know there's nothing to hydrate now
    leadCaptured = false;
    setHasMessages(false);
    setBusy(false); // bail out of any in-flight lock

    if (log) log.innerHTML = '';
    setMode('starter');

    if (starterIn) {
      starterIn.value = '';
      requestAnimationFrame(function () { starterIn.focus(); });
    }
  }

  // ------------------------------------------------------------------
  // Conversation replay (cross-page continuity)
  // ------------------------------------------------------------------
  // Rehydrates the log from the server when the drawer opens for the
  // first time on a new page. Replays as static bubbles + tile grids —
  // no SSE replay because the server already has the final state.

  function hydrateConversation() {
    if (!sessionId || !cfg.conversationUrl) return;

    var url = cfg.conversationUrl + '?session_id=' + encodeURIComponent(sessionId);
    fetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data) return;

        // Server signals an expired session by returning an empty
        // session_id alongside empty turns. Drop our stored id so the
        // next message starts a fresh conversation server-side too.
        if (!data.session_id) {
          try { sessionStorage.removeItem('hahn_ai_session'); } catch (e) {}
          sessionId = null;
          return;
        }

        // Set before rendering turns so a replayed lead form shows its
        // submitted/confirmation state when the lead was already captured.
        leadCaptured = !!data.lead_captured;

        if (!Array.isArray(data.turns) || !data.turns.length) return;

        data.turns.forEach(function (turn) {
          if (turn.user) {
            addUserMsg(turn.user);
          }
          if (turn.assistant) {
            // One bubble for the whole assistant message — matches the live
            // appendText behavior (paragraph breaks render as <br>s within).
            var bubble = addBotBubble();
            bubble.dataset.raw = turn.assistant;
            bubble.innerHTML   = renderMarkdown(turn.assistant);
          }
          if (turn.tiles && turn.tiles.length) {
            renderTiles(turn.tiles, null);
          }
          if (turn.ctas && turn.ctas.length) {
            renderCtas(turn.ctas);
          }
          if (turn.stats && turn.stats.length) {
            renderStats(turn.stats);
          }
          if (turn.lead_form) {
            renderLeadForm({ interest: turn.lead_form.interest || '' });
          }
        });

        if (data.turns.length) {
          setHasMessages(true);
          setMode('active');
          // After hydration, focus the bottom dock input.
          if (dockIn) dockIn.focus();
        }
      })
      .catch(function () { /* silently ignore — empty state still works */ });
  }

  // ------------------------------------------------------------------
  // Rendering helpers
  // ------------------------------------------------------------------

  function scrollToEnd() {
    var col = root.querySelector('.agent-mode__main');
    if (col) col.scrollTop = col.scrollHeight;
  }

  function addUserMsg(text) {
    var el = document.createElement('div');
    el.className = 'agent-mode__msg agent-mode__msg--user';
    el.textContent = text;
    log.appendChild(el);
    scrollToEnd();
    return el;
  }

  function addBotBubble() {
    var el = document.createElement('div');
    el.className = 'agent-mode__msg agent-mode__msg--bot';
    el.dataset.raw = '';
    log.appendChild(el);
    scrollToEnd();
    return el;
  }

  function addErrorMsg(text) {
    var el = document.createElement('div');
    el.className = 'agent-mode__msg agent-mode__msg--error';
    el.textContent = text;
    log.appendChild(el);
    scrollToEnd();
  }

  // Minimal markdown renderer — ported from the test page. Models
  // occasionally emit markdown even when asked for prose; rendering it
  // cleanly is better UX than showing literal `**` characters. Escapes
  // HTML first, then handles bold / italic / inline code / bullet lists.
  function renderMarkdown(raw) {
    var s = String(raw)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    s = s.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');

    var lines = s.split('\n');
    var out = [];
    var inList = false;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var m = line.match(/^\s*[-*]\s+(.*)$/);
      if (m) {
        if (!inList) { out.push('<ul>'); inList = true; }
        out.push('<li>' + m[1] + '</li>');
      } else {
        if (inList) { out.push('</ul>'); inList = false; }
        out.push(line);
      }
    }
    if (inList) out.push('</ul>');

    return out
      .join('\n')
      .replace(/<\/ul>\n/g, '</ul>')
      .replace(/\n<ul>/g, '<ul>')
      .replace(/\n/g, '<br>');
  }

  // Persistent activity indicator — "Thinking…" dots that stay visible
  // until ANY content (text or tiles) arrives. Updates label from
  // server-emitted status events. Lets the user know we're alive during
  // the 10–30s tool-iteration gaps.
  function createActivity(label) {
    var el = document.createElement('div');
    el.className = 'agent-mode__activity';
    el.innerHTML =
      '<span class="agent-mode__activity-dots">' +
        '<span></span><span></span><span></span>' +
      '</span>' +
      '<span class="agent-mode__activity-label"></span>';
    el.querySelector('.agent-mode__activity-label').textContent = label || 'Thinking…';
    log.appendChild(el);
    scrollToEnd();
    return el;
  }
  function setActivityLabel(el, text) {
    if (!el) return;
    var lbl = el.querySelector('.agent-mode__activity-label');
    if (lbl) lbl.textContent = text;
  }
  function removeActivity(el) {
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  function createTileLoader(label) {
    var el = document.createElement('div');
    el.className = 'agent-mode__tile-loader';
    el.innerHTML =
      '<span class="agent-mode__activity-dots">' +
        '<span></span><span></span><span></span>' +
      '</span>' +
      '<span>' + (label || 'Finding relevant pages…') + '</span>';
    log.appendChild(el);
    scrollToEnd();
    return el;
  }

  // Fetch tile data and replace the loader slot with the tile grid.
  // Atomic swap avoids the "loader visible, tiles appear after,
  // loader disappears" flicker.
  function renderTiles(ids, replaceNode) {
    if (!ids || !ids.length) {
      if (replaceNode && replaceNode.parentNode) replaceNode.parentNode.removeChild(replaceNode);
      return;
    }
    var url = cfg.tilesUrl + '?ids=' + encodeURIComponent(ids.join(','));
    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('tiles request failed: ' + res.status);
        return res.json();
      })
      .then(function (tiles) {
        if (!Array.isArray(tiles) || !tiles.length) {
          if (replaceNode && replaceNode.parentNode) replaceNode.parentNode.removeChild(replaceNode);
          return;
        }
        var wrap = document.createElement('div');
        wrap.className = 'agent-mode__tiles';
        tiles.forEach(function (t) { wrap.appendChild(buildTile(t)); });

        if (replaceNode && replaceNode.parentNode) {
          replaceNode.parentNode.replaceChild(wrap, replaceNode);
        } else {
          log.appendChild(wrap);
        }
        scrollToEnd();
      })
      .catch(function () {
        if (replaceNode && replaceNode.parentNode) replaceNode.parentNode.removeChild(replaceNode);
      });
  }

  function buildTile(t) {
    var a = document.createElement('a');
    a.className = 'agent-mode__tile';
    a.href = t.url;
    a.target = '_blank';
    a.rel = 'noopener';

    var thumb = document.createElement('div');
    thumb.className = 'agent-mode__tile-thumb';
    if (t.thumbnail) thumb.style.backgroundImage = "url('" + String(t.thumbnail).replace(/'/g, "\\'") + "')";
    a.appendChild(thumb);

    var body = document.createElement('div');
    body.className = 'agent-mode__tile-body';
    body.innerHTML =
      '<h3 class="agent-mode__tile-title">' + escapeHtml(t.title || '') + '</h3>' +
      '<p class="agent-mode__tile-excerpt">' + escapeHtml(t.excerpt || '') + '</p>';
    a.appendChild(body);
    return a;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ---------- CTA buttons ----------
  // Conversation facilitators, not links. Clicking a CTA sends its
  // configured message into the chat as if the visitor typed it; the
  // agent then responds normally (with tiles where relevant). A
  // "Let's Talk"-style CTA simply sends a connect-intent message, which
  // the agent handles by collecting contact info → submit_lead.
  // Rendered as a row in the same teal/straight-edge language.

  function renderCtas(items) {
    if (!items || !items.length) return;
    var wrap = document.createElement('div');
    wrap.className = 'agent-mode__ctas';
    items.forEach(function (cta) {
      var el = buildCta(cta);
      if (el) wrap.appendChild(el);
    });
    if (wrap.children.length) {
      log.appendChild(wrap);
      scrollToEnd();
    }
  }

  function buildCta(cta) {
    if (!cta || !cta.label) return null;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'agent-mode__cta';
    btn.textContent = cta.label;
    btn.addEventListener('click', function () {
      // Send the configured message (falls back to the label) and disable
      // the button so a second click doesn't fire the same message twice.
      // Pass the CTA label so the backend can tell the model that button
      // was clicked (so it won't re-offer it).
      var msg = cta.message || cta.label;
      btn.disabled = true;
      sendMessage(msg, { fromCta: cta.label });
    });
    return btn;
  }

  // ---------- stat grid ----------
  // A grid of cyan stat boxes (big value + small label), dropped into the
  // chat as its own block — like tiles. Data is curated (agency pool or a
  // page's stats), never generated, so values are always accurate.

  function renderStats(items) {
    if (!items || !items.length) return;
    // Dark card wrapper (like the chat response box) holding the grid of
    // teal stat boxes — so stats sit in their own container instead of
    // floating bare on the drawer background.
    var card = document.createElement('div');
    card.className = 'agent-mode__stats';
    var grid = document.createElement('div');
    grid.className = 'agent-mode__stats-grid';
    items.forEach(function (s) {
      if (!s || !s.value) return;
      var box = document.createElement('div');
      box.className = 'agent-mode__stat';

      var v = document.createElement('div');
      v.className = 'agent-mode__stat-value';
      v.textContent = s.value;

      var l = document.createElement('div');
      l.className = 'agent-mode__stat-label';
      l.textContent = s.label || '';

      box.appendChild(v);
      box.appendChild(l);
      grid.appendChild(box);
    });
    if (grid.children.length) {
      card.appendChild(grid);
      log.appendChild(card);
      scrollToEnd();
    }
  }

  // ---------- inline lead form ----------
  // Rendered when the agent calls render_lead_form. Collects name + email
  // (+ company) and posts to the lead endpoint, which saves locally and
  // pushes to Hubspot. On success the form swaps to a confirmation. If the
  // conversation already captured a lead (e.g. on replay after submitting),
  // we render the confirmation directly instead of a fresh form.

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function renderLeadForm(opts) {
    opts = opts || {};
    if (leadCaptured) {
      renderLeadConfirmation();
      return;
    }

    var interest = opts.interest || '';
    var form = document.createElement('form');
    form.className = 'agent-mode__lead';
    form.setAttribute('novalidate', 'novalidate');
    form.innerHTML =
      '<div class="agent-mode__lead-grid">' +
        '<label class="agent-mode__lead-field">' +
          '<span>Name</span>' +
          '<input type="text" name="name" autocomplete="name">' +
        '</label>' +
        '<label class="agent-mode__lead-field">' +
          '<span>Email</span>' +
          '<input type="email" name="email" autocomplete="email" required>' +
        '</label>' +
        '<label class="agent-mode__lead-field agent-mode__lead-field--full">' +
          '<span>Company</span>' +
          '<input type="text" name="company" autocomplete="organization">' +
        '</label>' +
      '</div>' +
      '<div class="agent-mode__lead-error" role="alert" hidden></div>' +
      '<div class="agent-mode__lead-actions">' +
        '<button type="submit" class="agent-mode__lead-submit">Send</button>' +
      '</div>';

    var errEl     = form.querySelector('.agent-mode__lead-error');
    var submitBtn = form.querySelector('.agent-mode__lead-submit');

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email   = form.querySelector('[name="email"]').value.trim();
      var name    = form.querySelector('[name="name"]').value.trim();
      var company = form.querySelector('[name="company"]').value.trim();

      if (!EMAIL_RE.test(email)) {
        showLeadError(errEl, 'Please enter a valid email address.');
        return;
      }
      hideLeadError(errEl);
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending…';

      fetch(cfg.leadUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce':   cfg.nonce,
        },
        body: JSON.stringify({
          session_id: sessionId || '',
          name:       name,
          email:      email,
          company:    company,
          interest:   interest,
        }),
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            return { status: res.status, body: body };
          });
        })
        .then(function (r) {
          if (r.status >= 200 && r.status < 300 && r.body && r.body.ok) {
            leadCaptured = true;
            swapToConfirmation(form, r.body.message);
          } else {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Send';
            showLeadError(errEl, (r.body && r.body.error) || 'Something went wrong — please try again.');
          }
        })
        .catch(function () {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Send';
          showLeadError(errEl, 'Network error — please try again.');
        });
    });

    log.appendChild(form);
    scrollToEnd();
    // Focus the email field — it's the one required field.
    var emailInput = form.querySelector('[name="email"]');
    if (emailInput) emailInput.focus();
  }

  function showLeadError(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }
  function hideLeadError(el) {
    if (el) el.hidden = true;
  }
  function swapToConfirmation(form, msg) {
    var conf = buildLeadConfirmation(msg);
    if (form.parentNode) form.parentNode.replaceChild(conf, form);
    scrollToEnd();
  }
  function renderLeadConfirmation(msg) {
    log.appendChild(buildLeadConfirmation(msg));
    scrollToEnd();
  }
  function buildLeadConfirmation(msg) {
    var conf = document.createElement('div');
    conf.className = 'agent-mode__lead-confirm';
    conf.textContent = msg || 'Thanks — your details are in. Someone from our team will reach out soon.';
    return conf;
  }

  // ------------------------------------------------------------------
  // SSE parser
  // ------------------------------------------------------------------

  function makeSseParser(onEvent) {
    var buf = '';
    var decoder = new TextDecoder('utf-8');

    return function (chunk) {
      buf += decoder.decode(chunk, { stream: true });
      var idx;
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        var raw = buf.slice(0, idx);
        buf = buf.slice(idx + 2);

        var dataLines = raw
          .split('\n')
          .filter(function (l) { return l.indexOf('data:') === 0; })
          .map(function (l) { return l.slice(5).replace(/^ /, ''); });
        if (!dataLines.length) continue;

        var payload = dataLines.join('\n');
        try { onEvent(JSON.parse(payload)); } catch (e) { /* skip malformed */ }
      }
    };
  }

  // ------------------------------------------------------------------
  // Per-turn event handler
  // ------------------------------------------------------------------

  function makeEventHandler(message) {
    addUserMsg(message);

    // Streamed assistant text is split into separate bubbles on blank lines,
    // so the three parts of the reply (answer / content-intro / follow-up)
    // render as distinct messages. textBubbles is sparse, indexed by
    // paragraph position within the current text run; resetTextRun() starts a
    // fresh run after content (tiles/stats/etc.) is surfaced, so post-content
    // text lands in new bubbles below it.
    var textBuffer = '';
    var textBubble = null;

    // One bubble per text run — the model's whole text response renders as a
    // single bubble (paragraph breaks within still show visually via the
    // <br> in renderMarkdown). resetTextRun() starts a fresh bubble after
    // content (tiles/stats/etc.) surfaces, so post-content text sits below
    // it as its own bubble.
    function appendText(delta) {
      textBuffer += (delta || '');
      if (!textBubble) textBubble = addBotBubble();
      textBubble.dataset.raw = textBuffer;
      textBubble.innerHTML   = renderMarkdown(textBuffer);
      scrollToEnd();
    }

    function resetTextRun() {
      textBuffer = '';
      textBubble = null;
    }

    // Persistent "generating" indicator. It stays pinned to the bottom of
    // the log (via CSS order) for the ENTIRE turn — through streaming text,
    // tile/stat loading, everything — and is only removed on done/error. So
    // the visitor always sees motion while a response is incoming, even
    // between sentences or while content loads.
    var activity = createActivity('Thinking…');
    var labelTimers = [];

    // The indicator's label is driven by 'phase' events from the backend
    // (AnthropicClient::stream_turn yields phases at iteration starts and
    // before each content tool). Each phase label describes what's being
    // PREPARED — the indicator is hidden the moment any content actually
    // streams. The cold-start fallbacks below cover the brief network gap
    // before the first phase event arrives.
    var phaseLabels = {
      initial:  'Thinking…',
      answer:   'Preparing initial response…',
      content:  'Finding useful content…',
      followup: 'Compiling follow-up…',
      lead:     'Loading contact form…'
    };
    var currentPhase = 'initial';

    labelTimers.push(setTimeout(function () {
      if (currentPhase === 'initial' && activity) setActivityLabel(activity, 'Looking through our pages…');
    }, 3500));
    labelTimers.push(setTimeout(function () {
      if (currentPhase === 'initial' && activity) setActivityLabel(activity, 'Putting together a response…');
    }, 8000));

    function clearColdTimers() {
      labelTimers.forEach(clearTimeout);
      labelTimers = [];
    }

    function setActivityVisible(visible) {
      if (!activity) return;
      activity.classList.toggle('is-hidden', !visible);
    }

    function setPhase(phase) {
      if (!activity) return;
      currentPhase = phase;
      setActivityLabel(activity, phaseLabels[phase] || 'Working…');
      setActivityVisible(true);
    }

    function setStatusLabel(text) {
      if (!activity) return;
      setActivityLabel(activity, text || '…');
      setActivityVisible(true);
    }

    // Always run when the turn ends — on a 'done'/'error' event OR when the
    // fetch promise settles without one (drop / timeout) — so the indicator
    // never outlives the response.
    function finalize() {
      clearColdTimers();
      if (activity) { removeActivity(activity); activity = null; }
    }

    var handler = function (ev) {
      switch (ev.type) {
        case 'session':
          sessionId = ev.id;
          try { sessionStorage.setItem('hahn_ai_session', sessionId); } catch (e) {}
          break;

        case 'phase':
          // The backend tells us what's being prepared next — show the matching
          // label. See AnthropicClient::stream_turn.
          clearColdTimers();
          setPhase(ev.phase);
          break;

        case 'status':
          // Tool-specific descriptive label, e.g. "Searching for X…".
          clearColdTimers();
          setStatusLabel(ev.message);
          break;

        case 'text':
          // Content is arriving — hide the indicator while it streams.
          clearColdTimers();
          setActivityVisible(false);
          appendText(ev.delta);
          break;

        case 'tiles':
          clearColdTimers();
          setActivityVisible(false);
          resetTextRun(); // text after tiles starts fresh bubbles below them
          // Insert a loader placeholder synchronously so the tiles land in
          // the right spot (between this sentence and the next), and so
          // there's a loading animation right where the content will appear.
          // renderTiles swaps it for the grid when the fetch resolves.
          var loader = createTileLoader('Finding relevant pages…');
          renderTiles(ev.ids || [], loader);
          break;

        case 'ctas':
          clearColdTimers();
          setActivityVisible(false);
          resetTextRun();
          renderCtas(ev.items || []);
          break;

        case 'stats':
          clearColdTimers();
          setActivityVisible(false);
          resetTextRun();
          renderStats(ev.items || []);
          break;

        case 'lead_form':
          clearColdTimers();
          setActivityVisible(false);
          resetTextRun();
          renderLeadForm({ interest: ev.interest || '' });
          break;

        case 'done':
          finalize();
          break;

        case 'error':
          finalize();
          addErrorMsg(ev.message || 'Something went wrong.');
          break;
      }
    };

    handler.finalize = finalize;
    return handler;
  }

  // ------------------------------------------------------------------
  // Submit flow
  // ------------------------------------------------------------------

  function sendMessage(message, opts) {
    if (!message) return;
    if (busy) return; // a response is still streaming — ignore new sends
    opts = opts || {};

    // Lock the input until this turn fully completes.
    setBusy(true);

    // First message transitions the shell into active state — the
    // input moves from the centered starter to the bottom dock and
    // the chips disappear. We do this BEFORE the network call so the
    // user sees the focus shift immediately.
    if (!hasMessages) {
      setHasMessages(true);
      setMode('active');
    }

    var handle = makeEventHandler(message);
    var parse  = makeSseParser(handle);
    abortCtl = new AbortController();

    var payload = {
      message: message,
      referrer_url: window.location.href,
    };
    if (sessionId) payload.session_id = sessionId;
    if (opts.fromCta) payload.from_cta = opts.fromCta;

    fetch(cfg.streamUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type':  'application/json',
        'Accept':        'text/event-stream',
        'X-WP-Nonce':    cfg.nonce,
      },
      body: JSON.stringify(payload),
      signal: abortCtl.signal,
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            handle({ type: 'error', message: body.error || body.message || ('Error: ' + res.status) });
          });
        }
        var reader = res.body.getReader();
        function pump() {
          return reader.read().then(function (r) {
            if (r.done) return;
            parse(r.value);
            return pump();
          });
        }
        return pump();
      })
      .catch(function (e) {
        if (e && e.name === 'AbortError') return;
        handle({ type: 'error', message: 'Network error: ' + (e && e.message || 'unknown') });
      })
      .then(function () {
        abortCtl = null;
        // Stream fully settled. Always tear down the activity indicator,
        // even if no 'done' arrived (drop / timeout) — that's what used to
        // leave the loader spinning forever. Idempotent with 'done'/'error'.
        if (handle && handle.finalize) handle.finalize();
        // Response fully finished (or errored/aborted) — unlock the input
        // and return focus so the visitor can ask their next question.
        setBusy(false);
        if (hasMessages && dockIn) {
          requestAnimationFrame(function () { dockIn.focus(); });
        }
      });
  }

  // ------------------------------------------------------------------
  // Wire up DOM events
  // ------------------------------------------------------------------

  // Open triggers — bubble, hero CTA, etc. — all use [data-agent-mode-open].
  // Delegated rather than per-element so triggers injected after load
  // (e.g. via shortcode AJAX) still work.
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-agent-mode-open]');
    if (!t) return;
    e.preventDefault();
    openDrawer();
  });

  // The Ask Bar (partials/agent-bar.php) hands off here: open the drawer and,
  // if a question was typed/clicked, submit it as the first message. This only
  // registers when the drawer is present (we returned early above otherwise),
  // and doesn't change any existing drawer behavior.
  document.addEventListener('hahn-agent:ask', function (e) {
    openDrawer();
    var msg = (e && e.detail && e.detail.message) ? String(e.detail.message).trim() : '';
    if (msg) sendMessage(msg);
  });

  // Exit buttons
  exitBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      closeDrawer();
    });
  });

  // "New conversation" buttons — reset to the starter state without
  // closing the drawer. Visibility is governed by CSS (.agent-mode--active
  // shows them, .agent-mode--starter hides them).
  newBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      startNewConversation();
    });
  });

  // Escape closes the drawer when it's open.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('is-open')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  // Forms — both the starter and the dock submit the same way.
  function bindForm(form, input) {
    if (!form || !input) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var msg = input.value.trim();
      if (!msg) return;
      input.value = '';
      sendMessage(msg);
    });

    // Enter submits; Shift+Enter inserts a newline. (Cmd/Ctrl+Enter also
    // submits, for muscle-memory.)
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        form.requestSubmit();
      }
    });
  }
  bindForm(starterFm, starterIn);
  bindForm(dockFm, dockIn);

  // Chips — click writes into the active input and submits.
  chips.forEach(function (chip) {
    chip.addEventListener('click', function (e) {
      e.preventDefault();
      var text = chip.dataset.prompt || chip.textContent.trim();
      if (!text) return;
      // Always use the starter input here since chips only show in
      // the empty state — sendMessage handles the swap to active.
      if (starterIn) starterIn.value = '';
      sendMessage(text);
    });
  });

})();
